/* One definition of how a chart looks, shared by the editor preview, the dashboard
   and the report HTML view. These three drew the same widget from three separate
   copies of the ApexCharts options, so the editor's restyle left the view pages on
   the old default look. */

// Slice/series palette. First entry is also the single-series colour for bar/line.
export const CHART_COLORS = ["#1a2e5a", "#f0a500", "#4ac9a4", "#c0c8d8", "#e05c5c", "#6c63ff", "#34a853"];

const AXIS_LABEL_COLOR = "#6b7a99";

/* Bar and line. A line is drawn as a filled area — the gradient is what makes it read
   as the new design rather than a bare stroke. */
export const buildCartesianChartOptions = ({ chartType, categories, xTitle, yTitle }) => {
  const isLine = String(chartType).toLowerCase() === "line";

  return {
    chart: {
      type: isLine ? "area" : String(chartType).toLowerCase(),
      toolbar: { show: false },
      zoom: { enabled: false },
      background: "transparent",
      fontFamily: "inherit",
    },
    /* The widget header already shows the name, so drawing it inside the chart
       repeated it and ate vertical space. */
    colors: [CHART_COLORS[0]],

    fill: isLine
      ? {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.25,
            opacityTo: 0.02,
            stops: [0, 90, 100],
          },
        }
      : { type: "solid" },

    stroke: isLine ? { curve: "smooth", width: 2 } : { show: false },

    ...(!isLine && {
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: "50%",
        },
      },
    }),

    dataLabels: { enabled: false },

    grid: {
      borderColor: "#f1f5f9",
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 0, right: 16, bottom: 0, left: 8 },
    },

    xaxis: {
      categories: categories || [],
      title: {
        text: xTitle,
        style: { fontSize: "11px", fontWeight: 500, color: AXIS_LABEL_COLOR },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        style: { fontSize: "11px", colors: AXIS_LABEL_COLOR },
        rotate: -45,
        trim: true,
        maxHeight: 80,
      },
    },

    yaxis: {
      title: {
        text: yTitle,
        style: { fontSize: "11px", fontWeight: 500, color: AXIS_LABEL_COLOR },
      },
      labels: {
        style: { fontSize: "11px", colors: AXIS_LABEL_COLOR },
      },
    },

    tooltip: {
      theme: "light",
      y: { formatter: (val) => `${val}` },
    },
  };
};

/* Pie. Drawn as a donut with the legend on the right — a plain pie is the old look. */
export const buildDonutChartOptions = ({ labels }) => ({
  chart: {
    type: "donut",
    toolbar: { show: false },
    background: "transparent",
    fontFamily: "inherit",
  },
  labels: labels || [],
  colors: CHART_COLORS,
  legend: {
    position: "right",
    fontSize: "12px",
    fontFamily: "inherit",
    labels: {
      colors: "#1a2e5a",
    },
    markers: {
      width: 10,
      height: 10,
      radius: 50,
    },
    itemMargin: {
      vertical: 4,
    },
    onItemClick: {
      toggleDataSeries: false, // ← disable slice toggle on legend click
    },
    onItemHover: {
      highlightDataSeries: false, // ← disable highlight on hover (also reduces re-renders)
    },
  },
  plotOptions: {
    pie: {
      donut: {
        size: "65%",
        labels: {
          show: true,
          name: {
            show: true,
            label: "Total",
            formatter: () => "Total",
            color: AXIS_LABEL_COLOR,
            fontSize: "13px",
          },
          value: {
            show: true,
            fontSize: "22px",
            fontWeight: 700,
            color: "#1a2e5a",
            formatter: () => "100%",
          },
          total: {
            show: true,
            label: "Total",
            color: AXIS_LABEL_COLOR,
            fontSize: "13px",
            formatter: () => "100%",
          },
        },
      },
    },
  },
  dataLabels: {
    enabled: false, // no percentage labels on slices
  },
  stroke: {
    width: 2,
    colors: ["#fff"], // white gap between slices
  },
  responsive: [
    {
      breakpoint: 480,
      options: {
        legend: { position: "bottom" },
      },
    },
  ],
  tooltip: {
    y: {
      formatter: (val) => `${val}`,
    },
  },
});
